import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { PercentageFormatInput } from 'v3/shared/components/Inputs/PercentageFormatInput';
import { IChangePercentageModalProps } from 'v3/shared/components/Modal/ChangePercentageModal/types';
import { percentageSchema } from 'v3/shared/schemas/changePercentage';
import { HeadlineLarge } from 'v3/shared/typography/HeadlineLarge';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { theme } from 'v3/styles/global-styles';
import { AlignButtons, AlignLeft, changeValuePaperPropsStyle, Container, StyledCloseIcon } from './styles';

export const ChangePercentageModal: React.FC<IChangePercentageModalProps> = ({ modal, onClose, onConfirm }) => {
  const { isMobile } = useBreakpoints();

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(percentageSchema),
    values: {
      percentageForSmartphone: modal.initialPercentageForSmartphone,
      percentageForAccessories: modal.initialPercentageForAccessories,
      percentageForInsurance: modal.initialPercentageForInsurance,
    },
  });

  const handleOnNextClick = () => {
    const { percentageForSmartphone, percentageForAccessories, percentageForInsurance } = getValues();
    onConfirm({
      maxPercentage: Number(percentageForSmartphone),
      maxPercentageInsurance: Number(percentageForInsurance),
      maxPercentageAccessories: Number(percentageForAccessories),
    });
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      onClose={handleClose}
      open={modal.active}
      aria-labelledby="customized-dialog-title"
      PaperProps={changeValuePaperPropsStyle}
    >
      <Container>
        <StyledCloseIcon onClick={handleClose} />
        <AlignLeft>
          <HeadlineLarge>Definir percentuais</HeadlineLarge>
          <SubtitleLarge color={theme.palette.secondary.main}>
            Ajuste o percentual de acréscimo que um consultor pode dar
          </SubtitleLarge>

          <SubtitleMedium>Porcentagem máxima do smartphone</SubtitleMedium>
          <InputFormController
            data-testid="input-percentage-smartphone"
            inputType="tel"
            formControl={control}
            formControlName="percentageForSmartphone"
            label="Porcentagem máxima"
            mask="0%"
            placeholder="20%"
            fullWidth={false}
            customMask={PercentageFormatInput}
          />

          <SubtitleMedium>Porcentagem máxima do seguro</SubtitleMedium>
          <InputFormController
            data-testid="input-percentage-insurance"
            inputType="tel"
            formControl={control}
            formControlName="percentageForInsurance"
            label="Porcentagem máxima"
            mask="0%"
            placeholder="35%"
            fullWidth={false}
            customMask={PercentageFormatInput}
          />

          <SubtitleMedium>Porcentagem máxima do acessório</SubtitleMedium>
          <InputFormController
            data-testid="input-percentage-accessories"
            inputType="tel"
            formControl={control}
            formControlName="percentageForAccessories"
            label="Porcentagem máxima"
            mask="0%"
            placeholder="30%"
            fullWidth={false}
            customMask={PercentageFormatInput}
          />
        </AlignLeft>

        <AlignButtons>
          {modal.activeButton && (
            <TextButton
              data-testid="text-button-cancel"
              size="small"
              $outline
              text={modal.cancelButton}
              handleClick={handleClose}
              fullWidth={isMobile}
            />
          )}
          {modal.activeButton && (
            <FlexButton
              data-testid="flex-button-save-button"
              size="small"
              text={modal.saveButton}
              handleClick={handleOnNextClick}
              fullWidth={isMobile}
              disabled={!isValid}
            />
          )}
        </AlignButtons>
      </Container>
    </Dialog>
  );
};
